import type { GeometryKey } from '../locales/keys';

export type TriangleMode = 'free-drag' | 'inequality' | 'congruence';
export type CongruenceCriterion = 'SSS' | 'SAS' | 'ASA' | 'AAS' | 'SSA';

export interface TrianglePreset {
  id: string;
  mode: TriangleMode;
  labelKey: GeometryKey;
  advancedOnly: boolean;
  // free-drag specific: initial vertex positions (SVG coords)
  vertices?: [{ x: number; y: number }, { x: number; y: number }, { x: number; y: number }];
  // inequality specific: initial side lengths
  sides?: [number, number, number];
  // congruence specific
  criterion?: CongruenceCriterion;
}

export const TRIANGLE_PRESETS: readonly TrianglePreset[] = [
  // Foundation presets
  {
    id: 'equilateral',
    mode: 'free-drag',
    labelKey: 'triangle.preset.equilateral',
    advancedOnly: false,
    vertices: [
      { x: 280, y: 330 },
      { x: 520, y: 330 },
      { x: 400, y: 122 },
    ],
  },
  {
    id: 'isosceles-right',
    mode: 'free-drag',
    labelKey: 'triangle.preset.isosceles-right',
    advancedOnly: false,
    vertices: [
      { x: 250, y: 330 },
      { x: 550, y: 330 },
      { x: 250, y: 30 },
    ],
  },
  {
    id: 'scalene-obtuse',
    mode: 'free-drag',
    labelKey: 'triangle.preset.scalene-obtuse',
    advancedOnly: false,
    vertices: [
      { x: 150, y: 340 },
      { x: 620, y: 340 },
      { x: 210, y: 120 },
    ],
  },
  // Advanced presets
  {
    id: 'euler-tour',
    mode: 'free-drag',
    labelKey: 'triangle.preset.euler-tour',
    advancedOnly: true,
    vertices: [
      { x: 240, y: 350 },
      { x: 560, y: 350 },
      { x: 350, y: 110 },
    ],
  },
  {
    id: 'ssa-two',
    mode: 'congruence',
    labelKey: 'triangle.preset.ssa-two',
    advancedOnly: true,
    criterion: 'SSA',
  },
];

export function isValidTrianglePreset(p: TrianglePreset): boolean {
  if (typeof p.id !== 'string' || p.id.length === 0) return false;
  const validModes: TriangleMode[] = ['free-drag', 'inequality', 'congruence'];
  if (!validModes.includes(p.mode)) return false;
  if (typeof p.labelKey !== 'string' || p.labelKey.length === 0) return false;
  if (typeof p.advancedOnly !== 'boolean') return false;
  if (p.vertices !== undefined) {
    if (!Array.isArray(p.vertices) || p.vertices.length !== 3) return false;
    for (const v of p.vertices) {
      if (typeof v.x !== 'number' || typeof v.y !== 'number') return false;
    }
  }
  if (p.sides !== undefined) {
    if (!Array.isArray(p.sides) || p.sides.length !== 3) return false;
    for (const s of p.sides) {
      if (typeof s !== 'number' || s <= 0) return false;
    }
  }
  if (p.criterion !== undefined) {
    const validCriteria: CongruenceCriterion[] = ['SSS', 'SAS', 'ASA', 'AAS', 'SSA'];
    if (!validCriteria.includes(p.criterion)) return false;
  }
  return true;
}

export function validateTrianglePresetIds(presets: readonly TrianglePreset[]): boolean {
  const ids = new Set<string>();
  for (const preset of presets) {
    if (ids.has(preset.id)) return false;
    ids.add(preset.id);
  }
  return true;
}
